import { Link } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { ArrowRight, User, PieChart, ListChecks, Gauge, Compass, Clock, BookOpen, ShieldCheck, FileText } from "lucide-react";
import { useSeo } from "@/lib/seo";

const COUPLE_IMG =
  "https://images.unsplash.com/photo-1508963493744-76fce69379c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const STEPS = [
  { icon: User, title: "Personal Information", copy: "Tell us a bit about yourself." },
  { icon: PieChart, title: "Financial Snapshot", copy: "Share key details about your finances." },
  { icon: ListChecks, title: "Planning Priorities", copy: "Identify what matters most." },
  { icon: Gauge, title: "Get Your Score", copy: "Receive your personalized readiness score." },
  { icon: Compass, title: "Next Steps", copy: "See helpful guidance and resources." },
];

const EXPECT = [
  { icon: Clock, title: "Takes about 3–5 minutes", copy: "The assessment is quick and easy to complete, with no complicated forms." },
  { icon: BookOpen, title: "Educational, not financial advice", copy: "Your results are for informational purposes only and are designed to help you think through important planning questions." },
  { icon: ShieldCheck, title: "No obligation", copy: "There's no cost and no commitment. You can explore your results at your own pace." },
  { icon: FileText, title: "Designed to organize planning questions", copy: "Your results help identify areas that appear well organized and areas that may deserve a closer look." },
];

export default function Landing() {
  useSeo({
    title: "Financial Future Readiness Assessment | Free Financial Health Score",
    description: "Take a quick financial readiness assessment covering savings, investments, taxes, protection, future income planning, and legacy preparation.",
    canonicalPath: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Financial Future Readiness Assessment",
      applicationCategory: "FinanceApplication",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: "Educational 0–100 financial readiness score across tax planning, future income, asset management, protection, and estate planning.",
    },
    jsonLdId: "home",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader />
      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="landing-main">

        {/* HERO — clean white/light-blue background, no full-bleed mountain */}
        <section style={{ background: "linear-gradient(180deg, #ffffff 0%, #F5F8FC 100%)" }}>
          <div className="summit-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="section-pill mb-6" data-testid="hero-eyebrow">Financial Future Readiness Assessment</div>
              <h1
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1.04, fontWeight: 600, letterSpacing: "-0.015em" }}
                data-testid="hero-title"
              >
                How Ready Is Your<br />Financial Future?
              </h1>
              <p className="mt-6 text-lg" style={{ color: "#17233D", maxWidth: 560, lineHeight: 1.55 }}>
                Take a quick assessment of your savings, investments, taxes, protection, future income planning, and legacy preparation.
              </p>
              <p className="mt-4 text-base" style={{ color: "#3a4d70", maxWidth: 560, lineHeight: 1.65 }}>
                Whether you're building your financial foundation, growing wealth, preparing for retirement, or already retired,
                this short assessment can help identify areas that appear well organized and areas that may deserve a closer look.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 sm:items-center">
                <Link to="/assessment" className="btn-primary" data-testid="hero-start-cta">
                  Take the Assessment <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                </Link>
                <div className="text-sm" style={{ color: "#173A6A" }}>Free · About 3–5 minutes · No obligation</div>
              </div>
              <p className="mt-6 text-sm" style={{ color: "#3a4d70", maxWidth: 620, lineHeight: 1.6 }}>
                Educational · No obligation · Results in minutes. Your score is based on the information you provide and is designed
                to help organize planning questions — not make financial recommendations.
              </p>
            </div>

            {/* Right column — framed couple image with navy overlay */}
            <div className="lg:col-span-6 relative">
              <div
                data-testid="hero-image-frame"
                style={{
                  background: "#ffffff",
                  padding: 10,
                  boxShadow: "0 10px 28px rgba(23, 35, 61, 0.08)",
                }}
              >
                <img
                  src={COUPLE_IMG}
                  alt="A couple seated on a bench looking out at a mountain lake"
                  loading="eager"
                  className="w-full h-[320px] md:h-[460px] object-cover"
                  style={{ display: "block" }}
                />
              </div>
              <div
                className="absolute hidden sm:block"
                style={{
                  bottom: -24, left: -20,
                  background: "#1E1968",
                  color: "#fff",
                  padding: "1.4rem 1.75rem",
                  maxWidth: 300,
                  boxShadow: "0 10px 28px rgba(23, 35, 61, 0.18)",
                }}
              >
                <div className="text-xs uppercase tracking-[0.17em]" style={{ color: "#8fbde0", fontWeight: 500 }}>
                  Five planning areas
                </div>
                <div className="font-serif mt-2" style={{ fontSize: "1.2rem", lineHeight: 1.3, fontWeight: 500 }}>
                  One clearer view of your financial future.
                </div>
                <div style={{ marginTop: 10, width: 32, height: 2, background: "#258FC4" }} />
              </div>
            </div>
          </div>
        </section>

        {/* A SIMPLE 5-PART ASSESSMENT */}
        <section style={{ background: "#F5F8FC", borderTop: "1px solid #e6edf5" }}>
          <div className="summit-container py-16 lg:py-20">
            <div className="text-center">
              <div className="section-pill mb-4 inline-block" data-testid="home-hiw-eyebrow">How It Works</div>
              <h2
                className="font-serif mx-auto"
                style={{ color: "#1E1968", fontSize: "clamp(1.8rem, 3.2vw, 2.5rem)", fontWeight: 600, lineHeight: 1.15, maxWidth: 740 }}
                data-testid="home-hiw-title"
              >
                A Simple, 5-Part Assessment
              </h2>
              <p className="mt-4 text-base mx-auto" style={{ color: "#3a4d70", maxWidth: 560, lineHeight: 1.6 }}>
                Get a clearer picture of your financial future in just a few minutes.
              </p>
            </div>

            {/* Desktop: 5 columns with icon circles + connecting line */}
            <ol
              className="hidden md:grid relative mt-14"
              style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "1.5rem", listStyle: "none", padding: 0, margin: "3.5rem 0 0 0" }}
              data-testid="home-hiw-steps"
            >
              <div aria-hidden="true" style={{ position: "absolute", top: 46, left: "10%", right: "10%", height: 1, background: "#c7d9ec", zIndex: 0 }} />
              {STEPS.map(({ icon: Icon, title, copy }, i) => (
                <li key={title} className="relative flex flex-col items-center text-center" style={{ zIndex: 1 }}>
                  <div className="font-serif" style={{ color: "#258FC4", fontSize: "0.95rem", fontWeight: 600, letterSpacing: "0.04em", marginBottom: 10 }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 72, height: 72, display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#eaf2fa", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 30, height: 30 }} strokeWidth={1.4} />
                  </span>
                  <div className="font-serif mt-5" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.25 }}>
                    {title}
                  </div>
                  <p className="mt-2 text-sm" style={{ color: "#3a4d70", lineHeight: 1.55, maxWidth: 200 }}>{copy}</p>
                </li>
              ))}
            </ol>

            {/* Mobile: vertical stack */}
            <ol className="md:hidden relative mt-10" style={{ listStyle: "none", margin: "2.5rem 0 0 0", padding: "0 0 0 72px" }}>
              <div aria-hidden="true" style={{ position: "absolute", top: 24, bottom: 24, left: 23, width: 1, background: "#c7d9ec" }} />
              {STEPS.map(({ icon: Icon, title, copy }, i) => (
                <li key={title} className="relative" style={{ marginBottom: i === STEPS.length - 1 ? 0 : "1.75rem" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", left: -72, top: 0,
                      width: 48, height: 48,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#eaf2fa", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 22, height: 22 }} strokeWidth={1.5} />
                  </span>
                  <div className="font-serif" style={{ color: "#258FC4", fontSize: "0.8rem", fontWeight: 600, letterSpacing: "0.04em" }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.25, marginTop: 2 }}>
                    {title}
                  </div>
                  <p className="mt-1.5 text-sm" style={{ color: "#3a4d70", lineHeight: 1.6 }}>{copy}</p>
                </li>
              ))}
            </ol>

            <div className="mt-14 flex justify-center">
              <Link to="/assessment" className="btn-primary" data-testid="secondary-start-cta">
                Take the Assessment <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section style={{ background: "#eaf2fa" }}>
          <div className="summit-container py-16 lg:py-20">
            <div className="text-center">
              <div className="section-pill mb-4 inline-block">What to Expect</div>
              <h2
                className="font-serif mx-auto"
                style={{ color: "#1E1968", fontSize: "clamp(1.75rem, 3vw, 2.3rem)", fontWeight: 600, lineHeight: 1.15, maxWidth: 740 }}
              >
                Straightforward, Helpful, and Confidential
              </h2>
              <p className="mt-4 text-base mx-auto" style={{ color: "#3a4d70", maxWidth: 680, lineHeight: 1.6 }}>
                Our assessment is designed to be a simple, low-pressure way to organize your thoughts and get educational insights.
              </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {EXPECT.map(({ icon: Icon, title, copy }) => (
                <div key={title} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "0 0 auto",
                      width: 48, height: 48,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#ffffff", color: "#1E1968", borderRadius: 999, border: "1px solid #cfe0f1",
                    }}
                  >
                    <Icon style={{ width: 22, height: 22 }} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.05rem", fontWeight: 600, lineHeight: 1.3 }}>
                      {title}
                    </div>
                    <p className="mt-2 text-sm" style={{ color: "#17233D", lineHeight: 1.6 }}>{copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRIVACY & SECURITY */}
        <section style={{ background: "#ffffff", borderTop: "1px solid #e6edf5" }} data-testid="privacy-section">
          <div className="summit-container py-14">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-4">
                <div className="section-pill mb-4">Before you begin</div>
                <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 600, lineHeight: 1.2 }}>
                  Your Privacy &amp; Security
                </h2>
              </div>
              <div className="lg:col-span-8 space-y-4 text-base" style={{ color: "#17233D", maxWidth: 760, lineHeight: 1.65 }}>
                <p>
                  This assessment asks for general financial information such as approximate assets, expenses, debt, account types, and planning
                  preferences. <strong>Please do not enter Social Security numbers, account numbers, passwords, tax identification numbers, or other
                  highly sensitive information.</strong>
                </p>
                <p style={{ color: "#3a4d70" }}>
                  Your responses are used to calculate your educational readiness score. If you choose to submit your contact information after
                  viewing your results, your assessment responses may be securely stored with your contact information so the planning team can
                  follow up on your request.
                </p>
                <div className="pt-2">
                  <Link to="/assessment" className="btn-secondary" data-testid="privacy-start-cta">
                    I understand — begin the assessment <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Financial Planning Resources — subtle links to educational SEO pages */}
        <section style={{ background: "#F5F8FC" }} data-testid="resources-section">
          <div className="summit-container py-14">
            <div className="max-w-2xl">
              <div className="section-pill mb-4"><BookOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" aria-hidden="true" />Financial Planning Resources</div>
              <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 600 }}>
                Educational guides for every stage
              </h2>
            </div>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/how-the-score-works" className="summit-card hover:border-[#258FC4] transition-colors" data-testid="resource-how">
                <div className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: "#258FC4", fontWeight: 500 }}>How It Works</div>
                <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>How Your Score Is Calculated</div>
                <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>The five categories, what they measure, and what the score does not mean.</p>
              </Link>
              <Link to="/financial-planning-20s-30s" className="summit-card hover:border-[#258FC4] transition-colors" data-testid="resource-20s30s">
                <div className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: "#258FC4", fontWeight: 500 }}>Guide · 20s &amp; 30s</div>
                <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>Building a Financial Foundation</div>
                <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>Cash flow, debt, saving habits, account types, and early protection basics.</p>
              </Link>
              <Link to="/financial-planning-40s-50s" className="summit-card hover:border-[#258FC4] transition-colors" data-testid="resource-40s50s">
                <div className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: "#258FC4", fontWeight: 500 }}>Guide · 40s &amp; 50s</div>
                <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>Wealth Building Meets Retirement Preparation</div>
                <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>Bringing goals, taxes, investments, protection, and estate documents together.</p>
              </Link>
              <Link to="/retirement-readiness-guide" className="summit-card hover:border-[#258FC4] transition-colors" data-testid="resource-retirement">
                <div className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: "#258FC4", fontWeight: 500 }}>Guide · Retirement</div>
                <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.3 }}>Retirement Readiness Guide</div>
                <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>Spending, predictable income, withdrawals, inflation, longevity, and protection.</p>
              </Link>
            </div>
          </div>
        </section>

      </main>
      <MinimalFooter />
    </div>
  );
}
